import * as React from 'react';
import {
  AddPhotoSheet,
  AddTile,
  Alert,
  BottomSheet,
  BottomSheetContent,
  Button,
  PhotoList,
  PhotoStrip,
  Stack,
} from '@relay-towing/ui';
import { usePlan } from '../../hooks/usePlan';
import { useVehicleImages } from '../../hooks/useVehicleImages';
import {
  capturePhoto,
  pickMedia,
  type PickedMedia,
} from '../../lib/nativeBridge';
import { FeatureUpsell } from '../plan/FeatureUpsell';
import type { Adder } from '../shell/adder';
import { PhotoSheet, QuickCommentSheet } from './PhotoSheet';

const SKIPPED: Record<string, string> = {
  tooLarge: 'was over the 50 MB limit',
  unreadable: 'could not be read',
  limit: 'was past the 10 a time',
};

/**
 * The call's photos, as the Figma draws them (455:58970): a strip of the
 * photos at their own width with "3 Photos" on the first, or the dashed
 * Add photos tile while there are none. With a photo, more are added from
 * the Photos list's Add more (the owner's call, 2026-10-22). Add asks camera, library or video; the picking goes through the
 * native bridge (a file input in a browser). On a plan without video the
 * video choice opens the upsell instead of hiding, so a member can find out
 * it exists.
 *
 * Tapping the strip opens the Photos panel (455:59091): every photo one
 * under another, each with a quick comment in its corner, pinched to look
 * closer, and Add more at its foot. A photo tapped there opens on its own
 * (PhotoSheet, 568:124106): its comments, swiped sideways to the rest, and
 * for a member who may change the vehicle, Delete and Comment. The strip
 * had a heading with its own Add, opened a photo straight from the strip,
 * and reached the panel only past three photos, none of which the Figma
 * draws.
 */
export const CallPhotos = React.forwardRef<
  Adder,
  {
    vehicleId: string;
    /** May add photos; the server refuses anyone else. */
    editable?: boolean;
  }
>(function CallPhotos({ vehicleId, editable = true }, ref) {
  const { vehicleImages, uploadImage, deleteImage } =
    useVehicleImages(vehicleId);
  const plan = usePlan();
  const allowVideo = plan.includes('video');
  const [adding, setAdding] = React.useState(false);
  React.useImperativeHandle(ref, () => ({ add: () => setAdding(true) }), []);
  const [upsell, setUpsell] = React.useState(false);
  const [viewing, setViewing] = React.useState<number | null>(null);
  const [all, setAll] = React.useState(false);
  const [commenting, setCommenting] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const upload = async ({ files, skipped }: PickedMedia) => {
    setNotice(
      skipped.length
        ? `${skipped.length} not added: ${skipped
            .map((item) => SKIPPED[item.reason] ?? item.reason)
            .join(', ')}.`
        : null
    );
    for (const file of files) {
      try {
        await uploadImage.mutateAsync({ vehicleId, file });
      } catch {
        setNotice('A photo did not upload. Try adding it again.');
      }
    }
  };

  // The upload shows the picture straight away from a local blob while it
  // sends; those tiles say so.
  const photos = vehicleImages.map((src) => ({
    id: src,
    src,
    status: src.startsWith('blob:') ? ('sending' as const) : undefined,
  }));

  const open = (id: string) =>
    setViewing(photos.findIndex((photo) => photo.id === id));

  const sheet = (
    <PhotoSheet
      vehicleId={vehicleId}
      photos={photos}
      index={viewing}
      onIndexChange={setViewing}
      onClose={() => setViewing(null)}
      editable={editable}
      deleting={deleteImage.isPending}
      onDelete={(imageUrl) => deleteImage.mutateAsync({ vehicleId, imageUrl })}
    />
  );

  const addVideo = () =>
    allowVideo
      ? void pickMedia({ allowVideo: true }).then(upload)
      : setUpsell(true);

  return (
    <Stack gap={4}>
      {photos.length > 0 ? (
        <PhotoStrip
          photos={photos}
          countLabel={`${photos.length} ${photos.length === 1 ? 'Photo' : 'Photos'}`}
          onOpen={() => setAll(true)}
        />
      ) : editable ? (
        // Once there is a photo, more are added from the Photos list's Add
        // more, so the call does not carry two ways to the same sheet.
        <AddTile width="block" onClick={() => setAdding(true)}>
          Add photos
        </AddTile>
      ) : null}
      {notice ? <Alert tone="warning">{notice}</Alert> : null}
      <AddPhotoSheet
        open={adding}
        onOpenChange={setAdding}
        onTakePhoto={() => void capturePhoto().then(upload)}
        onChooseFromLibrary={() => void pickMedia({ allowVideo }).then(upload)}
        onAddVideo={addVideo}
      />
      <FeatureUpsell
        open={upsell}
        onOpenChange={setUpsell}
        feature="video"
        title="Upgrade your plan"
      />
      <BottomSheet open={all} onOpenChange={setAll}>
        <BottomSheetContent
          title="Photos"
          size="full"
          footer={
            editable ? (
              <Button
                variant="secondary"
                size="xl"
                pill
                // The add sheet is the screen's, not the list's: put the list
                // away first so the two are not drawn flat on each other.
                onClick={() => {
                  setAll(false);
                  setAdding(true);
                }}
              >
                Add more
              </Button>
            ) : undefined
          }
        >
          <PhotoList
            photos={photos}
            onOpen={open}
            // A photo still sending has no address to hang a comment on yet.
            onComment={(id) =>
              photos.find((photo) => photo.id === id)?.status
                ? undefined
                : setCommenting(id)
            }
          />
          {/* Opened from the list, these stack over it rather than beside. */}
          {all ? sheet : null}
          <QuickCommentSheet
            vehicleId={vehicleId}
            src={commenting}
            onClose={() => setCommenting(null)}
            editable={editable}
          />
        </BottomSheetContent>
      </BottomSheet>
      {all ? null : sheet}
    </Stack>
  );
});
